'use client';

import React, { createContext } from 'react';
import { useTranslate } from '@tolgee/react';

import { Toast } from '~/shared/components/Toast';
import { TOAST_DURATION } from '~/shared/constants/toasts';
import { randomUUID } from '~/shared/utils/randomUUID';

export type ToastContextData = {
  add: (message: Omit<ToastMessage, 'id'> | string) => void;
};

const ToastContext = createContext({} as ToastContextData);

type ToastProviderProps = {
  children: React.ReactNode;
};

export type ToastMessage = {
  id: string;
  title?: string;
  description?: string;
  type?: React.ComponentProps<typeof Toast>['variant'];
  children?: React.ReactNode;
  icon?: React.ReactNode;
};

export function ToastProvider({ children }: ToastProviderProps) {
  const { t } = useTranslate();

  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

  const add = React.useCallback(
    (message: Omit<ToastMessage, 'id'> | string) => {
      const toast =
        typeof message === 'string' ? { description: message } : message;

      setToasts(state => [...state, { id: randomUUID(), ...toast }]);
    },
    []
  );

  function handleToastOpenChange(open: boolean, toastId: string) {
    const isClosing = !open;

    if (isClosing) {
      setTimeout(
        () => setToasts(state => state.filter(item => item.id !== toastId)),
        500
      );
    }
  }

  const providerValue = React.useMemo(() => ({ add }), [add]);

  return (
    <Toast.Provider
      duration={TOAST_DURATION}
      swipeDirection="right"
      label={t('common.notification')}
    >
      <ToastContext.Provider value={providerValue}>
        {children}
      </ToastContext.Provider>

      {toasts.map(toast => {
        let toastTitle = toast.title ?? '';
        let toastDescription = toast.description;

        if (!toastTitle && toast.type === 'error') {
          toastTitle = t('common.error.toast_title');
        }

        if (
          !toastTitle &&
          (toast.type === 'success' || toast.type === 'warning') &&
          toastDescription
        ) {
          toastTitle = toastDescription;
          toastDescription = undefined;
        }

        if (!toastTitle && !toastDescription) {
          toastTitle = t('common.error.toast_title', 'Notification');
        }

        return (
          <Toast
            key={toast.id}
            title={toastTitle}
            description={toastDescription}
            variant={toast.type}
            onOpenChange={open => handleToastOpenChange(open, toast.id)}
            closeAriaLabel={t('common.close_toast')}
            icon={toast.icon}
            isDetailed
          >
            {toast.children}
          </Toast>
        );
      })}

      <Toast.DetailedViewport
        className="bottom-16"
        label={`${t('common.notifications')} ({hotkey})`}
      />
    </Toast.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
